import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  CssBaseline,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import PlaceIcon from "@mui/icons-material/Place";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useNavigate } from "react-router-dom";

import { getPricingHolidayListUseCase } from "../domain/usecases/getPricingHolidayListUseCase";
import { deletePricingHolidayUseCase } from "../domain/usecases/deletePricingHolidayUseCase";
import Topbar from "../../../components/topbar/TopBar";

const MONTHS = [
  { value: 1, label: "Enero" },
  { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" },
  { value: 6, label: "Junio" },
  { value: 7, label: "Julio" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" },
  { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" },
  { value: 12, label: "Diciembre" },
];

function getMonthFromHolidayDate(value) {
  if (!value) {
    return 0;
  }

  const parts = value.split("-");

  if (parts.length !== 3) {
    return 0;
  }

  if (parts[0].length === 4) {
    return Number(parts[1]);
  }

  return Number(parts[1]);
}

function formatHolidayDate(value) {
  if (!value) {
    return "--";
  }

  const parts = value.split("-");

  if (parts.length !== 3) {
    return value;
  }

  if (parts[0].length === 4) {
    const [year, month, day] = parts;
    return `${day}-${month}-${year}`;
  }

  return value;
}

function getLocationLabel(holiday) {
  const segments = [
    holiday.country,
    holiday.department,
    holiday.province,
  ].filter(Boolean);

  return segments.length > 0 ? segments.join(" / ") : "Sin ubicacion";
}

function getMonthHolidays(holidays, selectedMonth) {
  return holidays
    .filter(
      (item) => getMonthFromHolidayDate(item.holidayDate) === selectedMonth,
    )
    .sort((left, right) => {
      const leftDate = new Date(left.holidayDate);
      const rightDate = new Date(right.holidayDate);
      return leftDate.getTime() - rightDate.getTime();
    });
}

function MonthTabs({ months, selectedMonth, onChange }) {
  return (
    <Tabs
      value={selectedMonth}
      onChange={(event, value) => onChange(value)}
      variant="fullWidth"
      sx={{
        "& .MuiTab-root": {
          minHeight: 54,
          textTransform: "none",
          fontSize: "0.98rem",
          fontWeight: 700,
          color: "#000",
        },
        "& .MuiTab-root.Mui-selected": {
          color: "#000",
          fontWeight: 800,
          fontSize: "1.105rem",
        },
        "& .MuiTabs-indicator": {
          backgroundColor: "#000",
        },
      }}
    >
      {months.map((month) => (
        <Tab key={month.value} value={month.value} label={month.label} />
      ))}
    </Tabs>
  );
}

export default function PricingHolidayListPage() {
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState(1);
  const [pricingHolidays, setPricingHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [message, setMessage] = useState({
    open: false,
    type: "success",
    text: "",
  });

  const loadPricingHolidays = async () => {
    try {
      setLoading(true);
      setError("");
      setPricingHolidays(await getPricingHolidayListUseCase());
    } catch (requestError) {
      setError(
        requestError.message ||
          "No se pudo cargar la lista de fechas festivas.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPricingHolidays();
  }, []);

  const selectedMonthHolidays = useMemo(
    () => getMonthHolidays(pricingHolidays, selectedMonth),
    [pricingHolidays, selectedMonth],
  );

  const selectedMonthLabel =
    MONTHS.find((month) => month.value === selectedMonth)?.label || "";

  const handleOpenDeleteDialog = (holiday) => {
    setDeleteTarget(holiday);
  };

  const handleCloseDeleteDialog = () => {
    if (deleting) {
      return;
    }

    setDeleteTarget(null);
  };

  const handleDeletePricingHoliday = async () => {
    if (!deleteTarget?.id) {
      return;
    }

    try {
      setDeleting(true);
      await deletePricingHolidayUseCase(deleteTarget.id);
      setPricingHolidays((prev) =>
        prev.filter((holiday) => holiday.id !== deleteTarget.id),
      );
      setMessage({
        open: true,
        type: "success",
        text: "Fecha festiva eliminada correctamente.",
      });
      setDeleteTarget(null);
    } catch (requestError) {
      setMessage({
        open: true,
        type: "error",
        text: requestError.message || "No se pudo eliminar la fecha festiva.",
      });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <CssBaseline />
      <main className="container">
        <Topbar />
        <Box
          sx={{
            minHeight: "100vh",
            color: "#000",
            "& .MuiTypography-root": {
              color: "#000",
            },
            p: 1,
          }}
        >
          <Stack spacing={3}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                overflow: "hidden",
                border: "1px solid rgba(16, 24, 40, 0.08)",
              }}
            >
              <Box
                sx={{
                  px: { xs: 2, md: 3 },
                  py: { xs: 2.5, md: 3 },
                  background:
                    "linear-gradient(135deg, #0f3d3e 0%, #165c53 55%, #2d8b74 100%)",
                  color: "#fff",
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  spacing={2}
                >
                  <Box>
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      sx={{ color: "#fff !important" }}
                    >
                      FECHAS FESTIVAS
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => navigate("/admin/precio-feriado")}
                    sx={{
                      px: 2.25,
                      py: 1,
                      color: "#fff",
                      bgcolor: "rgba(255,255,255,0.16)",
                      borderRadius: 2,
                      textTransform: "none",
                      boxShadow: "none",
                      "& .MuiButton-startIcon": {
                        color: "#fff",
                      },
                      "& .MuiSvgIcon-root": {
                        color: "#fff",
                      },
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.24)",
                        boxShadow: "none",
                      },
                    }}
                  >
                    Agregar Fecha
                  </Button>
                </Stack>
              </Box>

              <Box sx={{ px: { xs: 1, md: 2 }, pt: 1.5, bgcolor: "#fff" }}>
                <MonthTabs
                  months={MONTHS.slice(0, 6)}
                  selectedMonth={selectedMonth}
                  onChange={setSelectedMonth}
                />
                <Divider />
                <MonthTabs
                  months={MONTHS.slice(6, 12)}
                  selectedMonth={selectedMonth}
                  onChange={setSelectedMonth}
                />
              </Box>
            </Paper>

            {error ? <Alert severity="error">{error}</Alert> : null}

            <Paper
              elevation={0}
              sx={{
                borderRadius: 3,
                border: "1px solid rgba(16, 24, 40, 0.08)",
                overflow: "hidden",
                bgcolor: "#fff",
                p: 2,
              }}
            >
              <Box sx={{ px: { xs: 1, md: 2 }, py: 1 }}>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                  spacing={1.5}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={800}>
                      {selectedMonthLabel}
                    </Typography>
                  </Box>

                  <Chip
                    size="medium"
                    label={`${selectedMonthHolidays.length} registros`}
                    sx={{
                      borderRadius: 2,
                      bgcolor: "#edf7f3",
                      color: "#165c53",
                      fontWeight: 700,
                    }}
                  />
                </Stack>
              </Box>

              <Divider />

              <Box sx={{ p: { xs: 1, md: 2 } }}>
                {loading ? (
                  <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
                    <CircularProgress />
                    <Typography color="text.secondary">
                      Cargando fechas festivas...
                    </Typography>
                  </Stack>
                ) : null}

                {!loading && selectedMonthHolidays.length === 0 ? (
                  <Paper
                    variant="outlined"
                    sx={{
                      borderRadius: 3,
                      p: 4,
                      textAlign: "center",
                      bgcolor: "#fbfcfc",
                    }}
                  >
                    <Typography fontWeight={700}>
                      Sin fechas registradas
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 0.75 }}
                    >
                      No hay configuraciones disponibles para este mes.
                    </Typography>
                  </Paper>
                ) : null}

                {!loading && selectedMonthHolidays.length > 0 ? (
                  <Grid container spacing={2} sx={{ rowGap: 3 }}>
                    {selectedMonthHolidays.map((holiday) => (
                      <Grid
                        item
                        xs={12}
                        md={4}
                        xl={4}
                        key={holiday.id}
                        marginBottom={2}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            height: "100%",
                            minHeight: 220,
                            borderRadius: 3,
                            border: "1px solid rgba(20, 83, 45, 0.12)",
                            background:
                              "linear-gradient(180deg, #ffffff 0%, #f8fbfa 100%)",
                            p: 2.25,
                          }}
                        >
                          <Stack spacing={1.25}>
                            <Stack
                              direction="row"
                              justifyContent="space-between"
                              alignItems="flex-start"
                              spacing={1.5}
                            >
                              <Box>
                                <Typography variant="h6" fontWeight={800}>
                                  {holiday.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ mt: 0.5, color: "#000" }}
                                >
                                  {holiday.active ? "Activo" : "Inactivo"}
                                </Typography>
                              </Box>

                              <Chip
                                size="small"
                                label={`x${holiday.multiplier}`}
                                sx={{
                                  bgcolor: "#165c53",
                                  color: "#fff",
                                  fontWeight: 800,
                                }}
                              />
                            </Stack>

                            <Divider />

                            <Stack
                              direction="row"
                              spacing={1.25}
                              alignItems="center"
                            >
                              <CalendarMonthIcon sx={{ color: "#165c53" }} />
                              <Typography
                                fontWeight={700}
                                sx={{ color: "#000" }}
                              >
                                {formatHolidayDate(holiday.holidayDate)}
                              </Typography>
                            </Stack>

                            <Stack
                              direction="row"
                              spacing={1.25}
                              alignItems="center"
                            >
                              <PlaceIcon sx={{ color: "#165c53" }} />
                              <Typography
                                variant="body2"
                                sx={{ color: "#000" }}
                              >
                                {getLocationLabel(holiday)}
                              </Typography>
                            </Stack>

                            <Stack
                              direction="row"
                              spacing={1.25}
                              alignItems="center"
                            >
                              <TrendingUpIcon sx={{ color: "#c56a19" }} />
                              <Typography
                                variant="body2"
                                sx={{ color: "#000" }}
                              >
                                Multiplicador:{" "}
                                <Box
                                  component="span"
                                  sx={{ color: "#1f2937", fontWeight: 800 }}
                                >
                                  {holiday.multiplier}
                                </Box>
                              </Typography>
                            </Stack>

                            <Box sx={{ pt: 0.75 }}>
                              <Stack direction="row" spacing={1.25}>
                                <Button
                                  fullWidth
                                  variant="outlined"
                                  color="error"
                                  onClick={() =>
                                    handleOpenDeleteDialog(holiday)
                                  }
                                  sx={{
                                    borderRadius: 2,
                                    textTransform: "none",
                                    fontWeight: 700,
                                  }}
                                >
                                  Eliminar
                                </Button>
                                <Button
                                  fullWidth
                                  variant="contained"
                                  onClick={() =>
                                    navigate(
                                      `/admin/precio-feriado/actualizar/${holiday.id}`,
                                    )
                                  }
                                  sx={{
                                    borderRadius: 2,
                                    textTransform: "none",
                                    fontWeight: 700,
                                    bgcolor: "#165c53",
                                    color: "#fff",
                                    "&:hover": {
                                      bgcolor: "#12483f",
                                    },
                                  }}
                                >
                                  Actualizar
                                </Button>
                              </Stack>
                            </Box>
                          </Stack>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                ) : null}
              </Box>
            </Paper>
          </Stack>

          <Dialog
            open={Boolean(deleteTarget)}
            onClose={handleCloseDeleteDialog}
          >
            <DialogTitle>Eliminar fecha festiva</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Desea Eliminar esta Fecha Festiva?
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={handleCloseDeleteDialog} disabled={deleting}>
                No
              </Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeletePricingHoliday}
                disabled={deleting}
              >
                {deleting ? "Eliminando..." : "Si, eliminar"}
              </Button>
            </DialogActions>
          </Dialog>

          <Snackbar
            open={message.open}
            autoHideDuration={4000}
            onClose={() =>
              setMessage((prev) => ({
                ...prev,
                open: false,
              }))
            }
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Alert
              severity={message.type}
              variant="filled"
              onClose={() =>
                setMessage((prev) => ({
                  ...prev,
                  open: false,
                }))
              }
            >
              {message.text}
            </Alert>
          </Snackbar>
        </Box>
      </main>
    </>
  );
}
